import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Languages } from "lucide-react";
import { useLanguage, pick } from "@/lib/i18n";
import { NAV, PATHS } from "@/lib/translations";
import { LOGO_URL } from "@/lib/brand";
import { cn } from "@/lib/utils";

export default function Header() {
    const { lang, toggleLang } = useLanguage();
    const t = pick(NAV, lang);
    const [open, setOpen] = useState(false);
    const closeMenu = () => setOpen(false);

    const links = [
        { to: PATHS.home, label: t.home, key: "home" },
        { to: PATHS.privacy, label: t.privacy, key: "privacy" },
        { to: PATHS.terms, label: t.terms, key: "terms" },
        { to: PATHS.childSafety, label: t.childSafety, key: "child-safety" },
        { to: PATHS.cookies, label: t.cookies, key: "cookies" },
        { to: PATHS.contact, label: t.contact, key: "contact" },
        { to: PATHS.legal, label: t.legal, key: "legal" },
    ];

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-40 border-b border-jungle-700/10 bg-cream-50/85 backdrop-blur-xl"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-6 flex items-center justify-between h-16 sm:h-20">
                <Link
                    to={PATHS.home}
                    data-testid="logo-link"
                    className="flex items-center gap-2 group"
                >
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-white border-2 border-jungle-700/15 shadow-playful-sm transition-transform group-hover:-rotate-6 overflow-hidden">
                        <img
                            src={LOGO_URL}
                            alt="Dino Detective logo"
                            className="w-10 h-10 object-contain"
                        />
                    </span>
                    <span className="font-display text-xl sm:text-2xl font-bold text-jungle-900">
                        Dino Detective
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.key}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.key}`}
                            className={({ isActive }) =>
                                cn(
                                    "px-3 py-2 text-sm font-semibold rounded-full transition-colors",
                                    isActive
                                        ? "text-jungle-900 bg-amber-400/40"
                                        : "text-ink-700 hover:text-jungle-900 hover:bg-jungle-50"
                                )
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleLang}
                        data-testid="language-toggle"
                        aria-label="Toggle language"
                        className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full border-2 border-jungle-700 text-jungle-900 text-sm font-bold font-display hover:bg-jungle-700 hover:text-cream-50 transition-colors"
                    >
                        <Languages className="w-4 h-4" strokeWidth={2.5} />
                        <span className="uppercase">{lang}</span>
                    </button>

                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-jungle-700 text-cream-50"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-jungle-700/10 bg-cream-50"
                >
                    <nav className="max-w-6xl mx-auto px-5 py-4 grid gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.key}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-link-${l.key}`}
                                onClick={closeMenu}
                                className={({ isActive }) =>
                                    cn(
                                        "px-4 py-3 rounded-2xl text-base font-semibold",
                                        isActive
                                            ? "bg-jungle-700 text-cream-50"
                                            : "text-ink-900 hover:bg-jungle-50"
                                    )
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
}
